/** @jsx jsx */
import React, {
	Fragment,
	useCallback,
	useState,
	type PropsWithChildren,
	type ReactNode,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import { HydrationStatsReporter } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIsChromeless } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { LayoutRemoteStateUpdater } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/layout-remote-state-updater/index.tsx';
import { GlobalBottomRightCornerContainer } from '@atlassian/jira-layout-controller/src/ui/bottom-right-corner/container/index.tsx';
import { RenderOnClient } from '@atlassian/jira-ssr-toolkit/src/render-on-client.tsx';
import { PageLayout, Main } from '@atlassian/jira-navigation-system/src/index.tsx';
import {
	GLOBAL_CONTAINERS_MARK,
	GLOBAL_CONTAINERS_START_MARK,
	GLOBAL_CONTAINERS_END_MARK,
} from '@atlassian/jira-page-container-constants/src/index.tsx';
import { useJsmGracePeriodBanner } from '@atlassian/jira-jsm-grace-period-banner/src/controllers/index.tsx';
import { JSMGracePeriodBannerAsync } from '@atlassian/jira-jsm-grace-period-banner/src/ui/async.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useProjectTheme } from '@atlassian/jira-project-theme-page-container/src/controllers/project-theme/index.tsx';
import { sharedLayout } from '@atlassian/jira-shared-layout/src/sharedLayout.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { isSystemNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { JiraTopBannerPlacement } from '@atlassian/jira-top-banner-placement/src/index.tsx';
import { IntegrationsWhichRequireNavigationRoot } from '@atlassian/jira-shared-layout/src/ui/integrations/index.tsx';
import { getIsSoftwareThemingM2EnabledNoExposure } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { PreviewPanel } from '@atlassian/jira-preview-panels/src/PreviewPanel.tsx';
import { HydrationFinishedSetter } from '@atlassian/jira-ssr-toolkit/src/HydrationFinishedSetter.tsx';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { Banner } from '../banner/index.tsx';
import { GlobalComponents } from '../global-components/index.tsx';
import { GlobalContainers } from '../global-containers/main.tsx';
import { HorizontalNav } from '../horizontal-nav/index.tsx';
import { RightSidebar } from '../right-sidebar/index.tsx';
import { Sidebar } from '../sidebar/index.tsx';
import { TopNav } from '../topnav/index.tsx';

import { RedirectEntrypointContainer } from '../redirect/index.tsx';
import { GlobalThemeSetter } from './global-theme-setter/GlobalThemeSetter.tsx';
import { ProjectThemeSetter } from './project-theme-setter/ProjectThemeSetter.tsx';
import type { PageContainerProps } from './types.tsx';
import { PageContainerSuccessTracker } from './success-tracker/PageContainerSuccessTracker.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="page-container-v2-main-wrapper">{children}</UFOSegment>
	),
	Fragment,
);

const mainWrapperStyles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		' > span[data-ep-placeholder-id="route_entry_point"]': {
			// EntryPointPlaceholder span element will stop the height from being 100%.
			// this makes content height can be set to 100% even if the span is present.
			// https://jplat.jira.atlassian.cloud/browse/BLU-3362
			display: 'contents',
		},
	},
	// Delete when cleaning up getIsSoftwareThemingM2Enabled
	themedOld: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: Tokens.ELEVATION_SURFACE,
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode by blending with a black transparent background.
		backgroundBlendMode: 'darken',
	},
	themed: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: Tokens.ELEVATION_SURFACE,
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode and lightened in light mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundBlendMode: Tokens.ELEVATION_SURFACE_BLEND_MODE,
	},
});

/**
 * These test IDs are used in pollinator tests. Do not change lightly.
 */
const ROOT_TEST_ID = 'page-layout.root';
const MAIN_TEST_ID = 'page-layout.main';

const PageLayoutWithDefaultSideNavCollapsed = ({
	children,
	...props
}: React.ComponentPropsWithoutRef<typeof PageLayout>) => {
	const { isCollapsed } = useLeftSidebarState();
	const [defaultSideNavCollapsed] = useState(isCollapsed);

	return (
		<PageLayout {...props} defaultSideNavCollapsed={defaultSideNavCollapsed}>
			{children}
		</PageLayout>
	);
};

const DefaultLayout = ({ children }: PageContainerProps) => {
	const [isChromeless] = useIsChromeless();
	const [isFullscreen] = useIsFullscreen();

	const showBanner = !isChromeless && !isFullscreen;
	const showTopNav = !isChromeless && !isFullscreen;
	const showSidebar = !isChromeless && !isFullscreen;
	const showHorizontalNav = !isChromeless;
	const showRightSidebar = !isChromeless && !isFullscreen;
	const showLayoutRemoteStateUpdater = !isChromeless && !isFullscreen;

	// Not using `componentWithFG` because it was breaking mocking in tests for the FG off case
	const PageLayoutWithFG = fg('navx-full-height-sidebar')
		? PageLayoutWithDefaultSideNavCollapsed
		: PageLayout;

	return (
		<PageLayoutWithFG skipLinksLabel="" testId={ROOT_TEST_ID}>
			<RenderOnClient>
				<IntegrationsWhichRequireNavigationRoot />
			</RenderOnClient>
			{showBanner && (
				<JSErrorBoundary id="banner" packageName="jiraPageContainerV2" fallback="unmount">
					<Banner />
				</JSErrorBoundary>
			)}
			{showTopNav && (
				<JSErrorBoundary id="top-nav" packageName="jiraPageContainerV2" fallback="unmount">
					<TopNav />
				</JSErrorBoundary>
			)}
			{showSidebar && (
				<JSErrorBoundary id="sidebar" packageName="jiraPageContainerV2" fallback="flag">
					<Sidebar />
				</JSErrorBoundary>
			)}
			<MainWrapper>
				{showHorizontalNav && <HorizontalNav />}
				{children}
			</MainWrapper>
			{showRightSidebar && (
				<JSErrorBoundary id="right-sidebar" packageName="jiraPageContainerV2" fallback="unmount">
					<RightSidebar />
				</JSErrorBoundary>
			)}
			{fg('jira-glance-panel') && (
				<JSErrorBoundary fallback="unmount" id="preview-panel" packageName="jiraPageContainerV2">
					<PreviewPanel />
				</JSErrorBoundary>
			)}
			{showLayoutRemoteStateUpdater && (
				<JSErrorBoundary
					packageName="jiraPageContainerV2"
					id="layout-remote-state-updater"
					fallback="unmount"
				>
					<LayoutRemoteStateUpdater />
				</JSErrorBoundary>
			)}
		</PageLayoutWithFG>
	);
};

type MainWrapperProps = {
	children: ReactNode;
};

function MainWrapper(props: MainWrapperProps) {
	const { hasTheming } = useProjectTheme();
	const { isEnabled: isJsmGracePeriodBannerEnabled } = useJsmGracePeriodBanner();

	const currentRoute = useRouterRoute();
	const isIssueOrSystemAdminPage =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path);

	return (
		<Main isFixed testId={MAIN_TEST_ID}>
			<MaybeUFOSegment>
				<div
					data-vc="page-container-v2-main-wrapper"
					css={[
						mainWrapperStyles.root,
						hasTheming && !getIsSoftwareThemingM2EnabledNoExposure() && mainWrapperStyles.themedOld,
						hasTheming && getIsSoftwareThemingM2EnabledNoExposure() && mainWrapperStyles.themed,
					]}
				>
					{isJsmGracePeriodBannerEnabled && <JSMGracePeriodBannerAsync />}
					{isIssueOrSystemAdminPage && <JiraTopBannerPlacement />}
					{props.children}
				</div>
			</MaybeUFOSegment>
		</Main>
	);
}

const useRouterRoute = createRouterSelector((state) => state.route);
const Layout = (props: PageContainerProps) => {
	const { LayoutOverrideComponent } = useRouterRoute();
	if (LayoutOverrideComponent !== undefined) {
		return <LayoutOverrideComponent>{props.children}</LayoutOverrideComponent>;
	}
	return <DefaultLayout {...props} />;
};

export const PageContainer = (props: PageContainerProps) => {
	const { id, locale, messages, Router, routerLinkComponent } = props;
	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_CONTAINERS_MARK,
			GLOBAL_CONTAINERS_START_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			true,
			false,
		);
	}, []);

	return (
		<>
			<PerformanceMark metricKey={GLOBAL_CONTAINERS_START_MARK} />
			<GlobalContainers
				id={id}
				locale={locale}
				messages={messages}
				Router={Router}
				routerLinkComponent={routerLinkComponent}
			>
				{fg('redirect_all_new_jira_users_to_user_segmentation') && <RedirectEntrypointContainer />}
				<GlobalThemeSetter />
				<PerformanceMark
					metricKey={GLOBAL_CONTAINERS_END_MARK}
					onSet={pageContainerPerformanceTiming}
				/>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
					<ProjectThemeSetter />
					<Layout {...props} />
					<GlobalComponents sharedGlobalComponents={sharedLayout.globalComponents} />
					<GlobalBottomRightCornerContainer />
					<PageContainerSuccessTracker />
					{fg('jfp-magma-3742-report-hydration-stats') && <HydrationStatsReporter />}
					{fg('jfp-magma-4013-fix-hydration-edition-awareness') && <HydrationFinishedSetter />}
				</ContextualAnalyticsData>
			</GlobalContainers>
		</>
	);
};
